import { useEffect, useState } from 'react';
import { Box, Divider, Heading, Paragraph, Text, Toast, useToast } from '@zillow/constellation-10';
import { useApolloClient } from '@apollo/client';
import { useRouter } from 'next/router';
import { useFormContext } from 'react-hook-form';
import {
  useCappsPaymentWebApiHandler_startPaymentMethodSession,
  useCappsPaymentWebApiHandler_submitApplicationPayment,
} from '@zg-rentals/ts-capps-client-react-query';
import type { StartPaymentMethodSessionWebApiResponseDto } from '@zg-rentals/ts-capps-client';
import { ApplePayButton, PaymentMethodIframe } from '@zillow/metroid-components';
import type { SessionToken } from '@zillow/metroid-payment-method';
import type {
  SessionToken as MetroidComponentSessionToken,
  MetroidResponse,
  // @ts-expect-error - metroid-components types not exported for bundler resolution
} from '@zillow/metroid-components/dist/types/lib/types';
import { logError } from '@zg-rentals/log-error';
import { getCurrencyDisplayFromDollars } from '@zg-rentals/formatters';
import { LoadingText } from '@zg-rentals/loading-components';
import { useAuth } from '../AuthProvider';
import { useHasTax } from '../../utils/ab-tests';
import { TallHeightLoadingMask } from '../../styles/css-components';
import { useMetroidConfig } from '../../hooks/useMetroidConfig';
import { REQUESTING_CLIENT } from '../../constants/api';
import { getEditRoute } from '../../constants/urls';
import { FIELD_IDS, STEP_IDS } from '../../constants/wizard';
import { GetRentalApplicationDocument } from '../../graphql/queries';
import { RENTAL_APPLICATION_PRICE_LONG } from '../../constants/applicationsConstants';
import { useApplicationFee } from '../../hooks/useApplicationFee';
import { useIsInMobileWebView } from '../../hooks/useIsInMobileWebView';
import {
  ACTIONABLE_METROID_ERROR_CODES,
  getMetroidErrorMessage,
  METROID_ERROR_MESSAGES,
  METROID_ERROR_REGEX,
} from '../../constants/metroid';
import {
  emitApplePayButtonVisibleEvent,
  emitApplePayPurchaseReportsEvent,
  emitApplePaySuccessfulEvent,
  emitNonApplePaySuccessfulEvent,
  emitPaymentFormLoadingCompleteEvent,
  emitPaymentFormStartsLoadingEvent,
  emitPurchaseReportsEvent,
} from '../../analytics/clickstreamEvents';
import { useApplicationContextOrUndefined } from '../../contexts/ApplicationContext';

export function PaymentForm({
  applicationId,
  onSuccess,
  onError,
}: {
  applicationId: string;
  onSuccess?: () => void;
  onError?: () => void;
}) {
  const router = useRouter();
  const { enqueueToast } = useToast();
  const { getValues } = useFormContext();
  const { user: userData } = useAuth();
  const firstName = getValues('firstName');
  const lastName = getValues('lastName');
  const phone = getValues('phone');
  const street = getValues(FIELD_IDS.CURRENT_RESIDENCE_STREET);
  const city = getValues(FIELD_IDS.CURRENT_RESIDENCE_CITY);
  const zip = getValues(FIELD_IDS.CURRENT_RESIDENCE_ZIP);
  const state = getValues(FIELD_IDS.CURRENT_RESIDENCE_STATE);

  const hasAllRequiredData = !!(firstName && lastName && phone && street && city && state && zip);

  const needsName = !firstName || !lastName;
  const needsPhone = !phone;
  const needsAddress = !street || !city || !zip || !state;

  useEffect(() => {
    if (needsName) {
      enqueueToast(
        <Toast tone="info">You need to enter your first and last name in order to process your payment</Toast>,
      );
      router.push(
        getEditRoute({
          applicationId,
          stepId: STEP_IDS.LEGAL_NAME,
          nextStepId: STEP_IDS.PAYMENT_AND_REPORTS,
        }),
      );
    } else if (needsPhone) {
      enqueueToast(<Toast tone="info">You need to enter your phone number in order to process your payment</Toast>);
      router.push(
        getEditRoute({
          applicationId,
          stepId: STEP_IDS.CONTACT,
          nextStepId: STEP_IDS.PAYMENT_AND_REPORTS,
        }),
      );
    } else if (needsAddress) {
      enqueueToast(<Toast tone="info">You need to enter your address in order to process your payment</Toast>);
      router.push(
        getEditRoute({
          applicationId,
          stepId: STEP_IDS.CURRENT_RESIDENCE,
          nextStepId: STEP_IDS.PAYMENT_AND_REPORTS,
        }),
      );
    }
  }, [needsName, needsPhone, needsAddress]);

  const userId = userData?.userToken as string;

  return hasAllRequiredData && userId ? (
    <PaymentMetroidForm applicationId={applicationId} userId={userId} onSuccess={onSuccess} onError={onError} />
  ) : null;
}

type PaymentMethodSourceType = 'credit-card' | 'applePay';

type ApplePayLineItem = { label: string; amount: string; type?: 'final' | 'pending' };

const APPLE_PAY_LABEL = 'Zillow Rentals';

function PaymentMetroidForm({
  applicationId,
  userId,
  onSuccess,
  onError,
}: {
  applicationId: string;
  userId: string;
  onSuccess?: () => void;
  onError?: () => void;
}) {
  const { enqueueToast } = useToast();
  const { metroidConfigUrl, metroidHPPConfigId } = useMetroidConfig();

  const [sessionToken, setSessionToken] = useState<MetroidComponentSessionToken>();
  const [metroidAuthToken, setMetroidAuthToken] = useState<string>();
  const [isLoadingIFrame, setIsLoadingIFrame] = useState(true);
  const [isSubmittingPayment, setIsSubmittingPayment] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  const apolloClient = useApolloClient();
  const applicationContext = useApplicationContextOrUndefined();

  const translateSessionToken = (token: SessionToken): MetroidComponentSessionToken => {
    return {
      reference_key: token.referenceKey,
      secret: token.secret,
    };
  };

  const { mutate: startPaymentMethodMutation, isPending } = useCappsPaymentWebApiHandler_startPaymentMethodSession({
    onMutate: () => {
      emitPaymentFormStartsLoadingEvent(applicationContext);
    },
    onSuccess: async (response: StartPaymentMethodSessionWebApiResponseDto) => {
      setSessionToken(translateSessionToken(response.sessionToken));
      setMetroidAuthToken(response.metroidAuthToken);
    },
    onError: (error) => {
      logError({ error, context: 'PaymentMetroidForm - startPaymentMethodMutation error' });
      enqueueToast(<Toast tone="critical">We couldn’t load the payment form. Refresh the page and try again.</Toast>);
    },
  });

  const submitPaymentMutation = useCappsPaymentWebApiHandler_submitApplicationPayment();

  const { getValues } = useFormContext();
  const firstName = getValues(FIELD_IDS.FIRST_NAME);
  const lastName = getValues(FIELD_IDS.LAST_NAME);
  const email = getValues(FIELD_IDS.EMAIL);
  const phone = getValues(FIELD_IDS.PHONE);
  const street = getValues(FIELD_IDS.CURRENT_RESIDENCE_STREET);
  const city = getValues(FIELD_IDS.CURRENT_RESIDENCE_CITY);
  const zip = getValues(FIELD_IDS.CURRENT_RESIDENCE_ZIP);
  const state = getValues(FIELD_IDS.CURRENT_RESIDENCE_STATE);

  const hasTaxEnabled = useHasTax();

  const { previewPaymentData, feeDisplay, isLoading: isFeeLoading } = useApplicationFee({ applicationId });

  const applePayAmount =
    previewPaymentData && Number(previewPaymentData.invoiceAmount) > 0
      ? previewPaymentData.invoiceAmount
      : RENTAL_APPLICATION_PRICE_LONG;

  const applePayLineItems: Array<ApplePayLineItem> | undefined =
    hasTaxEnabled && previewPaymentData && Number(previewPaymentData.taxAmount) > 0
      ? [
          {
            label: APPLE_PAY_LABEL,
            amount:
              Number(previewPaymentData.amountWithoutTax) > 0
                ? previewPaymentData.amountWithoutTax
                : RENTAL_APPLICATION_PRICE_LONG,
            type: 'final',
          },
          { label: 'Sales tax', amount: previewPaymentData.taxAmount, type: 'final' },
        ]
      : undefined;

  function initializePaymentSession() {
    setSessionToken(undefined);
    setMetroidAuthToken(undefined);
    setIsLoadingIFrame(true);
    startPaymentMethodMutation({
      postData: {
        address1: street,
        city,
        country: 'United States',
        lastName,
        firstName,
        workEmail: email,
        homePhone: phone,
        postalCode: zip,
        paymentType: 'applications',
        stateOrProvince: state,
      },
    });
  }

  useEffect(() => {
    if (retryCount <= 2) {
      initializePaymentSession();
    }
  }, [retryCount]);

  function onResetPaymentSession(error: object) {
    onError?.();
    logError({ error, context: 'PaymentMetroidForm - onResetPaymentSession error' });
    initializePaymentSession();
  }

  async function onSubmitForm(paymentMethodSourceType: PaymentMethodSourceType): Promise<boolean> {
    if (!isSubmittingPayment) {
      emitPurchaseReportsEvent(applicationContext);
      if (paymentMethodSourceType === 'applePay') {
        // Ideally we should track this event on the "onpaymentmethodselected"
        // event but it's not exposed by metroid-components, so we track it here
        // a little bit later, this means that it will not be tracked (incorrect)
        // when the user selects a payment method but it is not authorized
        emitApplePayPurchaseReportsEvent(applicationContext);
      }
      setIsSubmittingPayment(true);
      let isPaymentSuccessful = false;
      try {
        const response = await submitPaymentMutation.mutateAsync({
          postData: {
            applicationId,
            requestingClient: REQUESTING_CLIENT,
            paymentMethodSourceType,
          },
        });
        if (response.isChargeSuccess || response.responseType === 'VALID_PAYMENT_EXISTS') {
          if (paymentMethodSourceType === 'applePay') {
            // Putting the trackEvent here, outside the isChargeSuccess if statement
            // because the if doesn't affect the isPaymentSuccessful variable
            emitApplePaySuccessfulEvent(applicationContext);
          } else {
            emitNonApplePaySuccessfulEvent(applicationContext);
          }

          if (response.isChargeSuccess) {
            enqueueToast(
              <Toast tone="success">Payment received. A payment confirmation email will be emailed to you.</Toast>,
            );
          }
          onSuccess?.();
          isPaymentSuccessful = true;
        } else if (response.isChargeDeclined) {
          enqueueToast(<Toast tone="critical">The payment was declined. Try again.</Toast>);
          onResetPaymentSession(response);
        } else {
          enqueueToast(<Toast tone="critical">The payment was not successful. Try again.</Toast>);
          onResetPaymentSession(response);
        }
      } catch (e) {
        enqueueToast(<Toast tone="critical">The payment was not successful. Try again.</Toast>);
        onResetPaymentSession(e);
      }

      if (isPaymentSuccessful) {
        await apolloClient.refetchQueries({ include: [GetRentalApplicationDocument] });
      }

      setIsSubmittingPayment(false);
      return isPaymentSuccessful;
    }
    return false;
  }

  async function onSubmitFailure(e: MetroidResponse<null>) {
    const { code, code_details } = e;
    let isClientError = false;
    if (
      code === ACTIONABLE_METROID_ERROR_CODES.EXPIRED_SESSION_TOKEN ||
      code === ACTIONABLE_METROID_ERROR_CODES.INVALID_SESSION_TOKEN
    ) {
      setRetryCount((currentRetryCount) => currentRetryCount + 1);
    } else if (code === ACTIONABLE_METROID_ERROR_CODES.ZEUI) {
      // ZE-UI errors are usually caused by the user
      if (code_details && METROID_ERROR_REGEX.DOWNSTREAM.test(code_details)) {
        // this specific error has nothing to do with user input, so only allow one retry in case it was a blip
        setRetryCount((currentRetryCount) => currentRetryCount + 1);
      } else {
        isClientError = true;
        enqueueToast(<Toast tone="critical">{getMetroidErrorMessage(e)}</Toast>);
        onResetPaymentSession(e);
      }
    } else {
      enqueueToast(<Toast tone="critical">{METROID_ERROR_MESSAGES.GENERIC}</Toast>);
      onResetPaymentSession(e);
    }
    logError({
      error: e,
      context: { location: 'PaymentMetroidForm', action: 'onSubmitFailure', isClientError, code, code_details },
      errorType: `SubmitPayment${isClientError ? 'ClientError' : 'ServerError'}`,
    });
  }

  const isInWebView = useIsInMobileWebView();
  const canApplePayBeShown = sessionToken && metroidAuthToken && !isInWebView && window.ApplePaySession;
  useEffect(() => {
    if (canApplePayBeShown) {
      emitApplePayButtonVisibleEvent(applicationContext);
    }
  }, [canApplePayBeShown]);

  return (
    <TallHeightLoadingMask loading={isPending || isLoadingIFrame || isSubmittingPayment}>
      {sessionToken && metroidAuthToken && (
        <Box
          css={{
            '& #z_hppm_iframe': {
              backgroundColor: '!transparent',
              width: '100%',
            },
          }}
        >
          <Box css={{ marginBottom: 'default' }}>
            <Heading level={3} textStyle="heading-xs" css={{ marginBottom: 'xs' }}>
              Provide your payment details
            </Heading>
            <Paragraph>
              Your credit or debit card will be charged a{' '}
              <LoadingText isLoading={isFeeLoading} loadingWidth="48px">
                {feeDisplay}
              </LoadingText>{' '}
              one-time fee
              {hasTaxEnabled && previewPaymentData && Number(previewPaymentData.taxAmount) > 0
                ? ` plus ${getCurrencyDisplayFromDollars({ amount: previewPaymentData.taxAmount })} in sales tax`
                : ''}
              . This covers application submissions for 30 days, plus credit and background checks from our partners,
              Experian and CIC (Contemporary Information Corporation).
            </Paragraph>
          </Box>
          <PaymentMethodIframe
            onSubmitSuccess={(_) => onSubmitForm('credit-card')}
            onSubmitFailure={onSubmitFailure}
            onRenderComplete={() => {
              emitPaymentFormLoadingCompleteEvent(applicationContext);
              setIsLoadingIFrame(false);
            }}
            sessionToken={sessionToken}
            metroidAccountId={{
              userId,
              brand: 'Zillow',
              businessUnit: 'Rentals Consumers',
            }}
            metroidEndpointUrl={metroidConfigUrl}
            metroidHPPConfigId={metroidHPPConfigId}
            setAsDefault
          />
          {!isInWebView && window.ApplePaySession && (
            <Box css={{ display: 'flex', flexDirection: 'column', gap: 'default', margin: 'tightest' }}>
              <Box css={{ display: 'grid', gridTemplateColumns: 'repeat(13, 1fr)', alignItems: 'center' }}>
                <Box css={{ gridColumnEnd: 'span 6' }}>
                  <Divider orientation="horizontal" role="presentation" />
                </Box>
                <Box css={{ gridColumnEnd: 'span 1' }}>
                  <center>
                    <Text>Or</Text>
                  </center>
                </Box>
                <Box css={{ gridColumnEnd: 'span 6' }}>
                  <Divider orientation="horizontal" role="presentation" />
                </Box>
              </Box>
              <Box
                css={{ '& apple-pay-button': { width: '100%' } }}
                style={{ '--apple-pay-button-height': '44px' } as React.CSSProperties}
              >
                <ApplePayButton
                  key={`${applePayAmount}-${applePayLineItems?.map((li) => li.amount).join(',') ?? ''}`}
                  brand="Zillow"
                  businessUnit="Rentals Consumers"
                  data={{
                    amount: applePayAmount,
                    label: APPLE_PAY_LABEL,
                    type: 'final',
                  }}
                  lineItems={applePayLineItems}
                  metroidAuthToken={metroidAuthToken}
                  metroidEndpointUrl={metroidConfigUrl}
                  processTransaction={(_) => onSubmitForm('applePay')}
                  onComplete={function ra() {}}
                  onError={onSubmitFailure}
                  type="pay"
                  setAsDefault
                />
              </Box>
            </Box>
          )}
        </Box>
      )}
    </TallHeightLoadingMask>
  );
}
